/**
 * Knowledge Bases Management Page
 * 
 * Displays and manages knowledge bases for organizing collections with 
 * shared permissions and taxonomy configurations.
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useI18n } from '@/i18n';
import { 
  Button, 
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Icon, 
  SearchInput, 
  Skeleton,
  Badge,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui';
import { toast } from 'sonner';
import { 
  useKnowledgeBases, 
  useDeleteKnowledgeBase,
  type KnowledgeBase,
  type KnowledgeBaseScopeType,
} from '@/hooks/use-knowledge-bases';
import { KnowledgeBaseModal } from '@/features/knowledge/components/KnowledgeBaseModal';

/**
 * Visual configuration for scope type badges
 */
const scopeTypeStyles: Record<KnowledgeBaseScopeType, { labelKey: string; colors: string }> = {
  shared: { 
    labelKey: 'admin.knowledge.scopeTypes.shared', 
    colors: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200',
  },
  client: { 
    labelKey: 'admin.knowledge.scopeTypes.client', 
    colors: 'bg-sky-100 text-sky-800 dark:bg-sky-900/50 dark:text-sky-200',
  },
  internal: { 
    labelKey: 'admin.knowledge.scopeTypes.internal', 
    colors: 'bg-slate-100 text-slate-800 dark:bg-slate-800/50 dark:text-slate-200',
  },
  restricted: { 
    labelKey: 'admin.knowledge.scopeTypes.restricted', 
    colors: 'bg-rose-100 text-rose-800 dark:bg-rose-900/50 dark:text-rose-200',
  },
};

/**
 * Placeholder cards shown during data loading
 */
function LoadingPlaceholder() {
  const placeholderItems = Array.from({ length: 6 }, (_, idx) => idx);
  
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {placeholderItems.map((idx) => (
        <Card key={`placeholder-${idx}`} className="p-4">
          <Skeleton className="h-10 w-10 rounded-lg mb-4" />
          <Skeleton className="h-5 w-3/4 mb-2" />
          <Skeleton className="h-4 w-1/2" />
        </Card>
      ))}
    </div>
  );
}

/**
 * Individual knowledge base card component
 */
function KnowledgeBaseCard({ 
  item, 
  onEdit, 
  onDelete, 
  onViewCollections,
  onClick,
  translations,
}: { 
  item: KnowledgeBase;
  onEdit: () => void;
  onDelete: () => void;
  onViewCollections: () => void;
  onClick: () => void;
  translations: { edit: string; viewCollections: string; delete: string; noDesc: string; scopeLabel: (key: string) => string };
}) {
  const scopeStyle = scopeTypeStyles[item.scope_type];
  const collectionLabel = item.collection_count === 1 ? 'collection' : 'collections';
  
  return (
    <Card className="hover:shadow-md transition-shadow relative group cursor-pointer" onClick={onClick}>
      <CardHeader className="pb-2">
        <div 
          className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10"
          onClick={(e) => e.stopPropagation()}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <Icon name="more-vertical" size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onEdit}>
                <Icon name="edit" size={14} className="mr-2" />
                {translations.edit}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onViewCollections}>
                <Icon name="folder-open" size={14} className="mr-2" />
                {translations.viewCollections}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                className="text-red-600 focus:text-red-600"
                onClick={onDelete}
              >
                <Icon name="trash-2" size={14} className="mr-2" />
                {translations.delete}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="flex items-center gap-3 pr-8">
          <div className="p-2 rounded-lg bg-indigo-50 dark:bg-indigo-900/30 shrink-0">
            <Icon name="database" size={20} className="text-indigo-600 dark:text-indigo-400" />
          </div>
          <CardTitle className="text-lg truncate">{item.name}</CardTitle>
        </div>
        <CardDescription className="line-clamp-2 mt-2">
          {item.description || translations.noDesc}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between">
          <Badge className={scopeStyle.colors}>
            {translations.scopeLabel(scopeStyle.labelKey)}
          </Badge>
          <span className="text-sm text-muted-foreground">
            {item.collection_count} {collectionLabel}
          </span>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Main Knowledge Bases Page Component
 */
export function KnowledgeBasesPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  
  // UI State
  const [filterText, setFilterText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [itemBeingEdited, setItemBeingEdited] = useState<KnowledgeBase | null>(null);
  const [itemToDelete, setItemToDelete] = useState<{ id: string; name: string } | null>(null);

  // Data fetching
  const queryResult = useKnowledgeBases({
    page: currentPage,
    page_size: 20,
    search: filterText || undefined,
  });

  const deleteMutation = useDeleteKnowledgeBase();

  // Event handlers
  const handleFilterChange = useCallback((value: string) => {
    setFilterText(value);
    setCurrentPage(1);
  }, []);

  const handleConfirmDelete = useCallback(async () => {
    if (!itemToDelete) return;
    
    try {
      await deleteMutation.mutateAsync(itemToDelete.id);
      toast.success(t('admin.knowledge.knowledgeBases.deleteSuccess'));
      setItemToDelete(null);
    } catch {
      toast.error(t('admin.knowledge.knowledgeBases.deleteError'));
    }
  }, [itemToDelete, deleteMutation]);

  const navigateToCollections = useCallback((kbId: string) => {
    navigate(`/knowledge/collections?kb=${kbId}`);
  }, [navigate]);

  // Derived state
  const items = queryResult.data?.data ?? [];
  const pagination = queryResult.data?.meta;
  const hasMorePages = pagination && pagination.total > pagination.page_size;

  // Translation strings for child components
  const cardTranslations = {
    edit: t('common.edit'),
    viewCollections: t('admin.knowledge.knowledgeBases.viewCollections'),
    delete: t('common.delete'),
    noDesc: t('admin.knowledge.knowledgeBases.noDescription'),
    scopeLabel: (key: string) => t(key),
  };

  return (
    <div className="p-6 space-y-6">
      {/* Page Header */}
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            {t('admin.knowledge.knowledgeBases.title')}
          </h1>
          <p className="text-muted-foreground mt-1">
            {t('admin.knowledge.knowledgeBases.subtitle')}
          </p>
        </div>
        <Button leftIcon="plus" onClick={() => setIsCreateModalOpen(true)}>
          {t('admin.knowledge.knowledgeBases.newKnowledgeBase')}
        </Button>
      </header>

      {/* Search Filter */}
      <div className="max-w-md">
        <SearchInput
          placeholder={t('admin.knowledge.knowledgeBases.searchPlaceholder')}
          value={filterText}
          onChange={(e) => handleFilterChange(e.target.value)}
          onClear={() => handleFilterChange('')}
        />
      </div>

      {/* Loading State */}
      {queryResult.isLoading && <LoadingPlaceholder />}

      {/* Error State */}
      {queryResult.isError && (
        <Card className="p-8 text-center">
          <Icon name="alert-circle" size={48} className="mx-auto text-destructive mb-4" />
          <h3 className="text-lg font-medium mb-2">
            {t('admin.knowledge.knowledgeBases.error.loadFailed')}
          </h3>
          <p className="text-muted-foreground mb-4">
            {queryResult.error instanceof Error 
              ? queryResult.error.message 
              : t('common.error')}
          </p>
          <Button variant="outline" onClick={() => queryResult.refetch()}>
            {t('admin.knowledge.knowledgeBases.error.retry')}
          </Button>
        </Card>
      )}

      {/* Empty State */}
      {!queryResult.isLoading && !queryResult.isError && items.length === 0 && (
        <Card className="p-8 text-center">
          <Icon name="database" size={48} className="mx-auto text-muted-foreground/50 mb-4" />
          <h3 className="text-lg font-medium mb-2">
            {t('admin.knowledge.knowledgeBases.empty.title')}
          </h3>
          <p className="text-muted-foreground mb-4">
            {filterText 
              ? t('admin.knowledge.knowledgeBases.empty.searchHint')
              : t('admin.knowledge.knowledgeBases.empty.createHint')
            }
          </p>
          {!filterText && (
            <Button leftIcon="plus" onClick={() => setIsCreateModalOpen(true)}>
              {t('admin.knowledge.knowledgeBases.newKnowledgeBase')}
            </Button>
          )}
        </Card>
      )}

      {/* Knowledge Bases Grid */}
      {!queryResult.isLoading && !queryResult.isError && items.length > 0 && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((kb) => (
              <KnowledgeBaseCard
                key={kb.id}
                item={kb}
                onEdit={() => setItemBeingEdited(kb)}
                onDelete={() => setItemToDelete({ id: kb.id, name: kb.name })}
                onViewCollections={() => navigateToCollections(kb.id)}
                onClick={() => navigate(`/knowledge/bases/${kb.id}`)}
                translations={cardTranslations}
              />
            ))}
          </div>

          {/* Pagination Controls */}
          {hasMorePages && pagination && (
            <nav className="flex items-center justify-center gap-3 pt-4">
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage <= 1}
                onClick={() => setCurrentPage((p) => p - 1)}
              >
                {t('common.previous')}
              </Button>
              <span className="text-sm text-muted-foreground px-2">
                {currentPage} / {pagination.total_pages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={currentPage >= pagination.total_pages}
                onClick={() => setCurrentPage((p) => p + 1)}
              >
                {t('common.next')}
              </Button>
            </nav>
          )}
        </>
      )}

      {/* Create Modal */}
      <KnowledgeBaseModal
        open={isCreateModalOpen}
        onOpenChange={setIsCreateModalOpen}
      />

      {/* Edit Modal */}
      {itemBeingEdited && (
        <KnowledgeBaseModal
          open={true}
          onOpenChange={() => setItemBeingEdited(null)}
          knowledgeBase={itemBeingEdited}
        />
      )}

      {/* Delete Confirmation */}
      <AlertDialog 
        open={!!itemToDelete} 
        onOpenChange={(open) => { if (!open) setItemToDelete(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('admin.knowledge.knowledgeBases.deleteDialog.title')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.knowledge.knowledgeBases.deleteDialog.description', { 
                name: itemToDelete?.name ?? '' 
              })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleteMutation.isPending 
                ? t('common.loading') 
                : t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
